/**
 * Lightweight geocoding for the Passport Map.
 *
 * Phase 1d uses a static lookup (offline, no API/token) covering common cities
 * + country centroids as a guaranteed fallback so the map never looks empty.
 * Later (1d.2) this can be backed by a geocode-once-and-cache service for full
 * coverage; the resolve() signature stays the same.
 *
 * Coordinates are [longitude, latitude] (react-simple-maps order).
 */

type LngLat = [number, number];

const norm = (s: string | null | undefined): string =>
  (s || '').trim().toLowerCase();

// City + country disambiguation for ambiguous names (checked before CITY_COORDS).
// Key format: "city|country" (both normalized/lowercased).
const CITY_COUNTRY_COORDS: Record<string, LngLat> = {
  'cambridge|usa': [-71.1097, 42.3736], // Cambridge, MA (Harvard/MIT)
  'cambridge|united states': [-71.1097, 42.3736],
  'cambridge|us': [-71.1097, 42.3736],
};

// Common cities (extend freely).
const CITY_COORDS: Record<string, LngLat> = {
  // Germany
  'aachen': [6.0839, 50.7753],
  'berlin': [13.405, 52.52],
  'munich': [11.582, 48.1351],
  'münchen': [11.582, 48.1351],
  'hamburg': [9.9937, 53.5511],
  'cologne': [6.9603, 50.9375],
  'köln': [6.9603, 50.9375],
  'frankfurt': [8.6821, 50.1109],
  'frankfurt am main': [8.6821, 50.1109],
  'stuttgart': [9.1829, 48.7758],
  'düsseldorf': [6.7735, 51.2277],
  'dusseldorf': [6.7735, 51.2277],
  'dortmund': [7.4653, 51.5136],
  'essen': [7.0116, 51.4556],
  'leipzig': [12.3731, 51.3397],
  'dresden': [13.7373, 51.0504],
  'hannover': [9.732, 52.3759],
  'hanover': [9.732, 52.3759],
  'nuremberg': [11.0767, 49.4521],
  'nürnberg': [11.0767, 49.4521],
  'bremen': [8.8017, 53.0793],
  'bonn': [7.0982, 50.7374],
  'mannheim': [8.4660, 49.4875],
  'karlsruhe': [8.4037, 49.0069],
  'freiburg': [7.8421, 47.999],
  'münster': [7.6261, 51.9607],
  'munster': [7.6261, 51.9607],
  'heidelberg': [8.6724, 49.3988],
  'darmstadt': [8.6512, 49.8728],
  'mainz': [8.2473, 49.9929],
  'wiesbaden': [8.2398, 50.0782],
  'bielefeld': [8.5325, 52.0302],
  'augsburg': [10.8978, 48.3705],
  'kiel': [10.1228, 54.3233],
  'erlangen': [11.0078, 49.5897],
  'ulm': [9.9876, 48.4011],
  'potsdam': [13.0645, 52.3906],
  'wolfsburg': [10.7865, 52.4227],
  'ingolstadt': [11.4237, 48.7665],
  'regensburg': [12.0974, 49.0134],
  'göttingen': [9.9351, 51.5413],
  'tübingen': [9.0576, 48.5216],
  // Europe
  'amsterdam': [4.9041, 52.3676],
  'rotterdam': [4.4777, 51.9244],
  'the hague': [4.3007, 52.0705],
  'eindhoven': [5.4697, 51.4416],
  'utrecht': [5.1214, 52.0907],
  'delft': [4.357, 52.0116],
  'london': [-0.1276, 51.5074],
  'cambridge': [0.1218, 52.2053],
  'oxford': [-1.2577, 51.752],
  'manchester': [-2.2426, 53.4808],
  'edinburgh': [-3.1883, 55.9533],
  'paris': [2.3522, 48.8566],
  'lyon': [4.8357, 45.764],
  'toulouse': [1.4442, 43.6047],
  'zurich': [8.5417, 47.3769],
  'zürich': [8.5417, 47.3769],
  'geneva': [6.1432, 46.2044],
  'lausanne': [6.6323, 46.5197],
  'basel': [7.5886, 47.5596],
  'bern': [7.4474, 46.948],
  'vienna': [16.3738, 48.2082],
  'wien': [16.3738, 48.2082],
  'graz': [15.4395, 47.0707],
  'madrid': [-3.7038, 40.4168],
  'barcelona': [2.1734, 41.3851],
  'milan': [9.19, 45.4642],
  'milano': [9.19, 45.4642],
  'rome': [12.4964, 41.9028],
  'roma': [12.4964, 41.9028],
  'turin': [7.6869, 45.0703],
  'stockholm': [18.0686, 59.3293],
  'gothenburg': [11.9746, 57.7089],
  'copenhagen': [12.5683, 55.6761],
  'oslo': [10.7522, 59.9139],
  'helsinki': [24.9384, 60.1699],
  'dublin': [-6.2603, 53.3498],
  'lisbon': [-9.1393, 38.7223],
  'porto': [-8.6109, 41.1496],
  'brussels': [4.3517, 50.8503],
  'antwerp': [4.4025, 51.2194],
  'luxembourg': [6.1296, 49.6116],
  'warsaw': [21.0122, 52.2297],
  'krakow': [19.945, 50.0647],
  'kraków': [19.945, 50.0647],
  'prague': [14.4378, 50.0755],
  'budapest': [19.0402, 47.4979],
  'athens': [23.7275, 37.9838],
  'istanbul': [28.9784, 41.0082],
  'moscow': [37.6173, 55.7558],
  // North America
  'new york': [-74.006, 40.7128],
  'new york city': [-74.006, 40.7128],
  'san francisco': [-122.4194, 37.7749],
  'mountain view': [-122.0838, 37.3861],
  'palo alto': [-122.143, 37.4419],
  'san jose': [-121.8863, 37.3382],
  'boston': [-71.0589, 42.3601],
  'st. louis': [-90.1994, 38.627],
  'saint louis': [-90.1994, 38.627],
  'seattle': [-122.3321, 47.6062],
  'austin': [-97.7431, 30.2672],
  'chicago': [-87.6298, 41.8781],
  'los angeles': [-118.2437, 34.0522],
  'san diego': [-117.1611, 32.7157],
  'washington': [-77.0369, 38.9072],
  'washington dc': [-77.0369, 38.9072],
  'atlanta': [-84.388, 33.749],
  'miami': [-80.1918, 25.7617],
  'denver': [-104.9903, 39.7392],
  'pittsburgh': [-79.9959, 40.4406],
  'philadelphia': [-75.1652, 39.9526],
  'toronto': [-79.3832, 43.6532],
  'vancouver': [-123.1207, 49.2827],
  'montreal': [-73.5673, 45.5017],
  'mexico city': [-99.1332, 19.4326],
  // Asia / Middle East / Oceania / LatAm
  'singapore': [103.8198, 1.3521],
  'tokyo': [139.6917, 35.6895],
  'kyoto': [135.7681, 35.0116],
  'osaka': [135.5023, 34.6937],
  'seoul': [126.978, 37.5665],
  'beijing': [116.4074, 39.9042],
  'shanghai': [121.4737, 31.2304],
  'shenzhen': [114.0579, 22.5431],
  'hong kong': [114.1694, 22.3193],
  'bangalore': [77.5946, 12.9716],
  'bengaluru': [77.5946, 12.9716],
  'mumbai': [72.8777, 19.076],
  'delhi': [77.1025, 28.7041],
  'new delhi': [77.209, 28.6139],
  'hyderabad': [78.4867, 17.385],
  'pune': [73.8567, 18.5204],
  'chennai': [80.2707, 13.0827],
  'dubai': [55.2708, 25.2048],
  'abu dhabi': [54.3773, 24.4539],
  'tel aviv': [34.7818, 32.0853],
  'sydney': [151.2093, -33.8688],
  'melbourne': [144.9631, -37.8136],
  'são paulo': [-46.6333, -23.5505],
  'sao paulo': [-46.6333, -23.5505],
  'rio de janeiro': [-43.1729, -22.9068],
  'buenos aires': [-58.3816, -34.6037],
};

// Country centroids (fallback when only the country is known).
const COUNTRY_COORDS: Record<string, LngLat> = {
  'germany': [10.45, 51.16],
  'netherlands': [5.29, 52.13],
  'united kingdom': [-1.5, 52.5],
  'uk': [-1.5, 52.5],
  'england': [-1.5, 52.5],
  'usa': [-98.5, 39.8],
  'united states': [-98.5, 39.8],
  'france': [2.21, 46.23],
  'switzerland': [8.23, 46.82],
  'austria': [14.55, 47.52],
  'spain': [-3.75, 40.46],
  'italy': [12.57, 41.87],
  'sweden': [18.64, 60.13],
  'denmark': [9.5, 56.26],
  'ireland': [-8.24, 53.41],
  'portugal': [-8.22, 39.4],
  'canada': [-106.35, 56.13],
  'singapore': [103.82, 1.35],
  'japan': [138.25, 36.2],
  'india': [78.96, 20.59],
  'china': [104.2, 35.86],
  'uae': [53.85, 23.42],
  'united arab emirates': [53.85, 23.42],
  'australia': [133.78, -25.27],
  'brazil': [-51.93, -14.24],
  'belgium': [4.47, 50.5],
  'luxembourg': [6.13, 49.81],
  'poland': [19.13, 51.92],
  'czech republic': [15.47, 49.82],
  'czechia': [15.47, 49.82],
  'hungary': [19.5, 47.16],
  'greece': [21.82, 39.07],
  'turkey': [35.24, 38.96],
  'russia': [105.32, 61.52],
  'norway': [8.47, 60.47],
  'finland': [25.75, 61.92],
  'mexico': [-102.55, 23.63],
  'south korea': [127.77, 35.91],
  'korea': [127.77, 35.91],
  'hong kong': [114.17, 22.32],
  'israel': [34.85, 31.05],
  'argentina': [-63.62, -38.42],
};

export interface GeoPoint {
  lngLat: LngLat;
  /** True when only a country centroid could be resolved (less precise). */
  approx: boolean;
}

/** Resolve to a precise city coordinate from the curated lookup, or null. */
export function resolveCity(city: string | null, country: string | null): GeoPoint | null {
  const c = norm(city);
  const co = norm(country);
  if (c && co && CITY_COUNTRY_COORDS[`${c}|${co}`]) {
    return { lngLat: CITY_COUNTRY_COORDS[`${c}|${co}`], approx: false };
  }
  if (c && CITY_COORDS[c]) return { lngLat: CITY_COORDS[c], approx: false };
  return null;
}

/** Resolve to a country centroid (less precise), or null. */
export function resolveCountry(country: string | null): GeoPoint | null {
  const co = norm(country);
  if (co && COUNTRY_COORDS[co]) return { lngLat: COUNTRY_COORDS[co], approx: true };
  return null;
}

/**
 * Resolve coordinates with a robust priority chain:
 *   1. curated city lookup (authoritative, handles disambiguation)
 *   2. LLM-predicted lat/lng (covers the long tail of cities worldwide)
 *   3. country centroid (last-resort fallback so the map isn't empty)
 *
 * `lat`/`lng` are the optional model predictions from the passport JSON.
 */
export function resolve(
  city: string | null,
  country: string | null,
  lat?: number | null,
  lng?: number | null,
): GeoPoint | null {
  const cityHit = resolveCity(city, country);
  if (cityHit) return cityHit;
  if (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !(Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01)
  ) {
    return { lngLat: [lng, lat], approx: false };
  }
  return resolveCountry(country);
}
